/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import {
  Autocomplete,
  AutocompleteCloseReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  PopperProps,
  TextField,
  Typography,
  createFilterOptions,
  css,
  useTheme,
} from '@mui/material';
import {
  ArrayUtils,
  DlMuiCaretDownIcon,
  DlMuiChip,
  DlMuiDeleteIcon,
  GetStylesFunction,
  StringUtils,
  TranslateUtils,
  isOptionEqualToValue,
  utils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import React, {JSXElementConstructor, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, IntlShape, useIntl} from 'react-intl';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig} from './LightFormatter';
import {LightInputLabel} from './LightInputLabel';
import {ValidateError} from './LightValidator';
import {FILTER_TYPE_MULTI_SELECT, FILTER_TYPE_SELECT} from 'src/js/lib/filtering';

const I18N = defineMessages({
  LOADING: {
    id: 'hierarchy.children.loading',
    defaultMessage: 'Loading ...',
    description: 'Dropdown label when options are loading',
  },
  NO_OPTIONS: {
    id: 'filter.dropDown.noOptions',
    defaultMessage: 'No matches found',
    description: 'Dropdown label when no options are available',
  },
  PLACEHOLDER_SHORT: {
    id: 'filter.dropDown.placeholderShort',
    defaultMessage: '...',
    description: 'compact placeholder in drop down input',
  },
});

export type Option = {
  id?: string | number;
  name?: string;
  label?: string;
  messageKey?: string;
  disabled?: boolean;
  parentId?: string | number;
  parentName?: string | number;
};
export type NormalizedOption<T = any> = {
  id?: string;
  label?: string;
  disabled?: boolean;
  parentId?: string | number;
  original: T;
};
export type FreeSoloValue = {freeSolo: string};
export function isFreeSolo(ref: any): ref is FreeSoloValue {
  const isObject = typeof ref === 'object' && Boolean(ref); // NOTE: typeof null === 'object';
  return isObject && 'freeSolo' in ref;
}
export type RenderTagCallback = (
  info: {
    optionId: string;
    label: string;
    original: any;
  },
  index: number,
  tagProps: any
) => JSX.Element;
export type LightSelectFilterProps<T = any> = {
  config: FilterConfig;
  optional?: boolean;
  options: Option[];
  value: any;
  onChange?: (newValue: any) => void;
  multi?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
  showChipsInSingleSelect?: boolean;
  freeSolo?: boolean;
  /** for example: `"OTHER - {}"` */
  freeSoloFormat?: string;
  /** for example: `"Add '{}'"` */
  addFreeSoloFormat?: string;
  getFreeSoloDisabled?: (option: FreeSoloValue) => boolean | undefined;
  loadingText?: string;
  noOptionsText?: string;
  placeholderText?: string;
  getOptionLabel?: (option: NormalizedOption<T>) => string;
  loading?: boolean;
  css?: SerializedStyles;
  autocompleteCss?: SerializedStyles;
  chipCss?: (selected: T | FreeSoloValue) => SerializedStyles | undefined;
  isDefaultValue?: boolean;
  error?: ValidateError;
  /** Max number of selected values shown as chips when the input is not focused; the rest collapses into "+N". Set -1 to always show all. */
  limitTags?: number;
  filterOptions?: (options: NormalizedOption<T>[], state: FilterOptionsState<NormalizedOption<T>>) => any[];
  maxWidth?: number | string;
  style?: React.CSSProperties;
  // props to passthrough
  disableListWrap?: boolean;
  isVirtualized?: boolean;
  PopperComponent?: JSXElementConstructor<PopperProps>;
  ListboxComponent?: JSXElementConstructor<React.HTMLAttributes<HTMLElement>>;
  ListboxProps?: any;
  disableCloseOnSelect?: boolean;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClose?: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
  renderOption?: (props: any, option: FreeSoloValue | NormalizedOption<T>, state: AutocompleteRenderOptionState, ownerState: any) => void;
  renderTag?: RenderTagCallback;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  /** Rendered after the given option in the flat (non-hierarchy) listbox; use to draw a group divider. */
  getOptionSeparator?: (option: NormalizedOption<T>) => React.ReactNode;
};

const defaultFilterOptions = createFilterOptions<NormalizedOption>();
export type SelectParams = {
  options: Option[];
  getOptionLabel: ((option: NormalizedOption) => string) | undefined;
  freeSolo: boolean | undefined;
  freeSoloFormat: string | undefined;
  addFreeSoloFormat: string | undefined;
  isVirtualized: boolean | undefined;
  chipCss?: LightSelectFilterProps<any>['chipCss'];
  renderTag?: RenderTagCallback;
};
export function getSelectOptions(intl: IntlShape, params: SelectParams | undefined) {
  const {
    options = [],
    getOptionLabel: _getOptionLabel,
    freeSolo,
    freeSoloFormat = '{}',
    addFreeSoloFormat, // default from translations below
    isVirtualized,
    chipCss,
    renderTag,
  } = params ?? {};
  const normalizedOptions: NormalizedOption[] = intl
    ? options.map((option) => ({
        id: String(option.id ?? option.name),
        label: option.messageKey ? utils.TranslateUtils.translate(option.messageKey, intl) : option.label,
        disabled: option.disabled,
        parentId: option.parentId ?? option.parentName,
        original: option,
      }))
    : [];
  const realIsOptionEqualToValue = (option: any, value: any) => {
    return isOptionEqualToValue(option, value, 'id');
  };
  const getOptionLabel = (option: any) => {
    if (isFreeSolo(option)) {
      return formatFreeSoloValue(option.freeSolo, freeSoloFormat);
    }
    if (_getOptionLabel) {
      return _getOptionLabel(option);
    }
    return option?.label ?? '';
  };
  const renderInfos = (selectedValues: any[]) => {
    return selectedValues.map((selectedValue: any) => {
      let optionId = '';
      let label = '?';
      if (isFreeSolo(selectedValue)) {
        optionId = selectedValue.freeSolo;
        label = getOptionLabel(selectedValue);
      } else {
        const option = normalizedOptions.find((v) => realIsOptionEqualToValue(v, selectedValue));
        optionId = option?.id ?? '';
        label = getOptionLabel(option);
      }
      return {optionId, label, original: selectedValue};
    });
  };
  const renderTags = (selectedValues: any[], getTagProps: (state: {index: number}) => any) =>
    renderInfos(selectedValues).map((info, index) => {
      const {optionId, label} = info;
      const tagProps = getTagProps({index});
      return renderTag ? (
        renderTag(info, index, tagProps)
      ) : (
        <DlMuiChip
          {...tagProps}
          css={chipCss && chipCss(info.original)}
          key={`${optionId}-${index}`}
          label={String(label)}
          deleteIcon={<DlMuiDeleteIcon />}
        />
      );
    });
  const renderOption = (props: any, option: FreeSoloValue | NormalizedOption, state: AutocompleteRenderOptionState) => {
    const {key} = props;
    const index = state.index ?? props['data-option-index']; // eslint-disable-line
    let label = getOptionLabel(option);
    if (freeSolo) {
      const isNewFreeSoloOption =
        isFreeSolo(option) && option.freeSolo === stripFreeSoloFormat(state.inputValue, freeSoloFormat) && !state.selected;
      if (isNewFreeSoloOption) {
        const format = addFreeSoloFormat ?? TranslateUtils.translate('global.addFreeSolo', intl);
        label = format.replace('{}', label);
      }
    }
    const className = classNames(props.className, 'light-select-filter-option');
    const virtualProps = isVirtualized ? {component: 'li', noWrap: true} : {};
    return (
      <Typography {...props} key={`${key}-${index}`} variant={'value'} className={className} {...virtualProps}>
        {label}
      </Typography>
    );
  };
  return {normalizedOptions, realIsOptionEqualToValue, getOptionLabel, renderInfos, renderTags, renderOption};
}
export type GetSelectOptions = ReturnType<typeof getSelectOptions>;

function getFreeSoloPrefix(freeSoloFormat: string | undefined) {
  const format = freeSoloFormat ?? '{}';
  const placeholder = '{}';
  const index = format.indexOf(placeholder);

  if (index < 0) return '';
  return format.slice(0, index);
}

function stripFreeSoloFormat(value: string, freeSoloFormat: string | undefined) {
  const prefix = getFreeSoloPrefix(freeSoloFormat);
  if (!prefix) return value;

  let result = value;
  while (result.startsWith(prefix)) {
    result = result.slice(prefix.length);
  }
  return result.trimStart();
}

function formatFreeSoloValue(value: string, freeSoloFormat: string | undefined) {
  const rawValue = stripFreeSoloFormat(value, freeSoloFormat);
  return (freeSoloFormat ?? '{}').replace('{}', rawValue);
}

export const LightSelectFilter = <T,>(props: LightSelectFilterProps<T>) => {
  const {
    config: _config,
    optional,
    options,
    value: valueOrUndefined,
    onChange,
    multi = false,
    isClearable = true,
    disabled = false,
    showChipsInSingleSelect = false,
    freeSolo = false,
    freeSoloFormat,
    addFreeSoloFormat,
    getFreeSoloDisabled = () => false,
    loadingText = I18N.LOADING.id,
    noOptionsText = I18N.NO_OPTIONS.id,
    // compact "..." for single and multi selects alike (PDW-167) — in multiselects it fits next to the chips
    placeholderText = I18N.PLACEHOLDER_SHORT.id,
    getOptionLabel: _getOptionLabel,
    loading = false,
    css,
    autocompleteCss,
    chipCss,
    isDefaultValue,
    error,
    limitTags = 1,
    filterOptions,
    maxWidth,
    style,
    disableListWrap,
    isVirtualized,
    PopperComponent,
    ListboxComponent,
    ListboxProps,
    disableCloseOnSelect,
    onInputChange,
    open,
    onOpen,
    onClose,
    renderOption,
    renderTag,
    renderGroup,
    getOptionSeparator,
  } = props;
  // config
  const filterType = multi || showChipsInSingleSelect ? FILTER_TYPE_MULTI_SELECT : FILTER_TYPE_SELECT;
  const config = {
    ..._config,
    ui: {..._config.ui, type: filterType},
  };
  // value
  const value = useMemo(() => {
    if (multi) {
      return valueOrUndefined ?? [];
    }
    if (showChipsInSingleSelect) {
      return valueOrUndefined ? [valueOrUndefined] : [];
    }
    return valueOrUndefined ?? null;
  }, [multi, showChipsInSingleSelect, valueOrUndefined]);

  const [localInputValue, setLocalInputValue] = useState<string | undefined>('');
  const rootAreaMouseDownTarget = useRef<EventTarget | null>(null);

  // CTRL/meta + select keeps the options list open (MUI built-in); keep the typed text too,
  // so the user can keep picking from the same filtered list
  const isKeepOpenSelect = (event: any) => multi && Boolean(event && (event.ctrlKey || event.metaKey));

  const handleLocalInputValue = (event: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => {
    // In multi mode MUI fires input 'reset' after every selection AND again (without an event)
    // when the controlled value updates; ignore them all and clear the text explicitly
    // in handleChange / handleAutocompleteClose, so that CTRL+select keeps the typed filter
    if (reason === 'reset' && multi) return;
    if (typeof onInputChange === 'function') {
      onInputChange(event, value, reason);
    }
    setLocalInputValue(value);
  };

  const handleAutocompleteClose = (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
    if (multi) {
      setLocalInputValue('');
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleChange = useCallback(
    (event, newValue) => {
      if (onChange) {
        if (multi) {
          onChange(newValue.length ? newValue.map((v) => v.id ?? v) : null);
        } else if (showChipsInSingleSelect) {
          const lastValue = newValue.length && newValue[newValue.length - 1];
          onChange(lastValue ? lastValue.id ?? lastValue : null);
        } else {
          onChange(newValue !== null ? newValue.id ?? newValue : null);
        }
      }
      if (!isKeepOpenSelect(event)) {
        setLocalInputValue('');
      }
    },
    [onChange, multi, showChipsInSingleSelect]
  );

  // options
  const intl = useIntl();
  const {
    normalizedOptions,
    realIsOptionEqualToValue,
    getOptionLabel,
    renderTags,
    renderOption: defaultRenderOption,
  } = getSelectOptions(intl, {
    options,
    getOptionLabel: _getOptionLabel,
    freeSolo,
    freeSoloFormat,
    addFreeSoloFormat,
    isVirtualized,
    chipCss,
    renderTag,
  });
  // styles
  const valueToRender = useMemo(() => {
    return multi || showChipsInSingleSelect
      ? value
      : normalizedOptions.find((option) => realIsOptionEqualToValue(option, value)) ?? (value || null);
  }, [multi, showChipsInSingleSelect, value, normalizedOptions, realIsOptionEqualToValue]);

  useEffect(() => {
    const isEmptyObject = freeSolo && value && typeof value === 'object' && Object.keys(value).length === 0;

    if (valueToRender === null || valueToRender === '' || isEmptyObject) {
      setLocalInputValue('');
    }
  }, [valueToRender]);

  // The trailing "..." of the focused editing view is glued to the LAST chip (PDW-167 follow-up),
  // so it can never end up alone on a wrapped row; the glue wrapper's flex metrics also seat the
  // input caret on the same row as the last chip's label (see getStyles). At rest the glue is
  // inert (dots hidden via CSS) and the input's own "..." placeholder serves the collapsed row.
  const renderTagsWithTrailingDots = (selectedValues: any[], getTagProps: (state: {index: number}) => any) => {
    const tags = renderTags(selectedValues, getTagProps) as JSX.Element[];
    if (!tags.length) return tags;
    const last = tags[tags.length - 1];
    tags[tags.length - 1] = (
      <span key='dl-last-tag-glue' className='dl-last-tag-glue'>
        {last}
        {/* hidden while typing — mirrors how the native placeholder disappears */}
        {localInputValue === '' && (
          <span className='dl-last-tag-dots'>{TranslateUtils.translate(I18N.PLACEHOLDER_SHORT.id, intl)}</span>
        )}
      </span>
    );
    return tags;
  };

  const baseRenderOption = renderOption ?? defaultRenderOption;
  const finalRenderOption = getOptionSeparator
    ? (optProps: any, option: FreeSoloValue | NormalizedOption<T>, state: AutocompleteRenderOptionState, ownerState: any) => {
        const node = baseRenderOption(optProps, option, state, ownerState);
        const separator = isFreeSolo(option) ? null : getOptionSeparator(option);
        if (!separator) return node;
        return (
          <React.Fragment key={`${optProps.key}-with-separator`}>
            {node}
            {separator}
          </React.Fragment>
        );
      }
    : baseRenderOption;

  const theme = useTheme();
  const hasValue = multi || showChipsInSingleSelect ? value.length > 0 : value !== null && typeof value !== 'undefined';
  let isOccupied = !isDefaultValue;
  if (typeof isDefaultValue === 'undefined') isOccupied = hasValue;
  if (isOccupied && !hasValue) isOccupied = false;
  const styles = getStyles({theme, params: {css, autocompleteCss, maxWidth}});

  return (
    <span
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      css={styles.autocompleteWrapper}
      data-name={config.name}
      style={style}
    >
      <Autocomplete
        disableListWrap={disableListWrap}
        PopperComponent={PopperComponent}
        ListboxComponent={ListboxComponent}
        ListboxProps={ListboxProps}
        data-name={'dl-mui-autocomplete--autocomplete'}
        disabled={disabled}
        popupIcon={<DlMuiCaretDownIcon />}
        multiple={multi || showChipsInSingleSelect}
        limitTags={limitTags}
        disableClearable={!isClearable}
        freeSolo={freeSolo}
        noOptionsText={TranslateUtils.translate(noOptionsText, intl)}
        loadingText={TranslateUtils.translate(loadingText, intl)}
        loading={loading}
        isOptionEqualToValue={(option, value) => realIsOptionEqualToValue(option, value)}
        css={styles.autocomplete}
        options={normalizedOptions as (NormalizedOption<T> | FreeSoloValue)[]}
        fullWidth
        getOptionLabel={getOptionLabel as any}
        getOptionDisabled={(option) => (isFreeSolo(option) ? getFreeSoloDisabled(option) ?? false : option.disabled ?? false)}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                onMouseDown: (event: React.MouseEvent) => {
                  rootAreaMouseDownTarget.current = event.target;
                },
                onClick: (event: React.MouseEvent) => {
                  // MUI toggles the popup when a click lands on the input's root area; when the
                  // limitTags chips expand on focus, the first click's mouseup can land on a chip
                  // that shifted under the cursor and the retargeted click would immediately close
                  // the just-opened popup — forward the toggle only for clicks that also STARTED
                  // on the root area
                  const mouseDownOnRootArea = rootAreaMouseDownTarget.current === event.currentTarget;
                  rootAreaMouseDownTarget.current = null;
                  if (mouseDownOnRootArea && params.InputProps && typeof (params.InputProps as any).onClick === 'function') {
                    (params.InputProps as any).onClick(event);
                  }
                },
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...(config.ui.label ? {} : {shrink: false}),
              }}
              label={<LightInputLabel text={config.ui.label} optional={optional} intl={intl} />}
              placeholder={TranslateUtils.translate(config.ui.placeholder ?? placeholderText, intl)}
              css={styles.textField}
              error={error?.bool}
              helperText={error?.string}
              data-occupied={isOccupied}
              data-disabled={Boolean(disabled)}
              data-error={Boolean(error?.bool)}
            />
          );
        }}
        renderTags={renderTagsWithTrailingDots}
        renderOption={finalRenderOption}
        renderGroup={renderGroup}
        onChange={handleChange}
        value={valueToRender}
        filterOptions={(options, state) => {
          const filtered: (NormalizedOption<T> | FreeSoloValue)[] = filterOptions
            ? filterOptions(options as NormalizedOption<T>[], state)
            : defaultFilterOptions(options as NormalizedOption<T>[], state);
          const {inputValue} = state;
          if (freeSolo) {
            const arrayValue = ArrayUtils.asArray(value);
            let haveInputValue = false;
            arrayValue.forEach((singleValue) => {
              if (isFreeSolo(singleValue)) {
                if (singleValue.freeSolo.includes(inputValue)) {
                  filtered.push(singleValue);
                  haveInputValue = true;
                }
              }
            });
            if (inputValue && !haveInputValue) {
              filtered.push({freeSolo: stripFreeSoloFormat(inputValue, freeSoloFormat)} as FreeSoloValue);
            }
          }
          return filtered;
        }}
        disableCloseOnSelect={disableCloseOnSelect}
        onInputChange={handleLocalInputValue}
        inputValue={localInputValue}
        open={open}
        onOpen={onOpen}
        onClose={handleAutocompleteClose}
        autoHighlight
      />
    </span>
  );
};

const getStyles: GetStylesFunction = (options) => {
  const maxWidth = options?.params?.maxWidth;
  return {
    // NOTE: we need a fixed width, so that we get the correct size with `flex-wrap: wrap`
    autocompleteWrapper: css(
      `
        display: flex;
        max-width: ${StringUtils.addPx(maxWidth === undefined ? '100%' : maxWidth)};
        width: ${StringUtils.addPx(maxWidth === undefined ? 362 : '100%')};
        padding: 8px 0;
      `,
      options?.params?.css
    ),
    autocomplete: css(
      {
        '& .MuiInputLabel-root': {
          zIndex: 11,
        },
        'div.MuiAutocomplete-tag': {
          maxWidth: '175px',
        },
        // Single-line resting state (PDW-167): while the input is NOT focused the selected chips
        // must not wrap — the chip shrinks (its label ellipsizes early) so the "+N" badge and the
        // "..." placeholder still fit on the same line even in narrow boxes. The focused state
        // keeps MUI's expand-all-chips-and-wrap editing view.
        '& .MuiInputBase-root:not(.Mui-focused)': {
          flexWrap: 'nowrap',
          overflow: 'hidden',
        },
        '& .MuiInputBase-root:not(.Mui-focused) div.MuiAutocomplete-tag': {
          minWidth: 0,
          flexShrink: 1,
          // 1px off the label→delete-icon gap: with the shrunk input below, short values like
          // "Střední" fit whole next to "+N" and "..." in the 176px boxes
          '& .MuiChip-label': {
            paddingRight: '7px',
          },
        },
        // At rest the input only shows the "..." placeholder, so MUI's 30px min-width + 12px padding
        // would steal width the chip needs in narrow boxes (176px "N.." instead of "Nízká") — shrink
        // the input to the dots. The focused input keeps MUI's sizing for typing.
        '& .MuiInputBase-root:not(.Mui-focused) input.MuiAutocomplete-input': {
          minWidth: '11px !important',
          padding: '2.5px 0 2.5px 3px !important',
        },
        // Glue wrapper around the LAST chip (PDW-167 follow-up): keeps the trailing "..." attached
        // to the chip and — in the focused editing view — seats the input caret on the same row.
        // flex-basis 0 keeps the glue "tiny" for flexbox line-breaking (which uses UNSHRUNK item
        // sizes), so glue + input always share a row, or wrap to a fresh row TOGETHER once even
        // min-width + the input's 30px don't fit; flex-grow then expands the label into the real
        // leftover space, capped at its own content width so no gap opens before the caret.
        '& .dl-last-tag-glue': {
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: 0,
        },
        '& .MuiInputBase-root.Mui-focused .dl-last-tag-glue': {
          flexGrow: 999,
          flexShrink: 1,
          flexBasis: '0%',
          minWidth: '80px',
          maxWidth: 'max-content',
        },
        // Focused but nothing typed yet: the input only hosts the caret, so it may stay on the last
        // chip's row even when MUI's 30px min-width + padding (42px) no longer fits there — otherwise
        // narrow boxes grow an extra row holding just the caret. Once the user types (placeholder no
        // longer shown) MUI's sizing returns and the input wraps to a row of its own if needed.
        '& .MuiInputBase-root.Mui-focused input.MuiAutocomplete-input:placeholder-shown': {
          minWidth: '14px !important',
          padding: '2.5px 2px 2.5px 4px !important',
        },
        '& .MuiInputBase-root.Mui-focused .dl-last-tag-glue div.MuiAutocomplete-tag': {
          maxWidth: '100%',
          minWidth: 0,
          flexShrink: 1,
        },
        '& .dl-last-tag-dots': {
          flexShrink: 0,
          opacity: 0.42,
          padding: '0 2px',
        },
        // The glued dots belong to the focused editing view only — at rest the input's "..."
        // placeholder serves the collapsed chip+badge row and the glue behaves like a plain tag.
        '& .MuiInputBase-root:not(.Mui-focused) .dl-last-tag-dots': {
          display: 'none',
        },
        // While the glued dots are visible the input's own "..." would double up — hide it.
        '& .MuiInputBase-root.Mui-focused:has(.dl-last-tag-glue) input::placeholder': {
          opacity: '0 !important',
        },
        // The "+N" overflow count MUI renders for limitTags (PDW-167): a rounded badge in the selected
        // filter tab's color (--dlTheme-500 — MERIE teal, HAIDI blue) with light text; keeps its size
        // when the chip next to it shrinks.
        'span.MuiAutocomplete-tag': {
          flexShrink: 0,
          height: '18px',
          lineHeight: '18px',
          padding: '0 5px',
          borderRadius: '4px',
          background: 'var(--dlTheme-500)',
          color: '#fff',
          fontSize: '12px',
          fontFamily: 'var(--dlFontFamilySemiBold)',
        },
      },
      options?.params?.autocompleteCss
    ),
    textField: css({
      '& .MuiOutlinedInput-root': {
        paddingRight: '56px !important',
      },
    }),
  };
};
